import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AuthenticationResult,
  type AccountInfo,
} from '@azure/msal-browser';
import msalConfig from '@/services/auth/authConfig';
import { currentUserProfile } from '@/services/auth/utils/authUtils';
import type { MsalWrapper } from '@/services/auth/types';
import { LoginInfoError } from '@/utils/errors/LoginInfoError';

// Singleton: Single MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Shared cache for in-flight token requests to prevent duplicate calls
const tokenRequestCache = new Map<
  string,
  Promise<AuthenticationResult | null>
>();

interface MsalError {
  errorCode?: string;
  code?: string;
  errorMessage?: string;
  message?: string;
}

/* eslint-disable unicorn/consistent-function-scoping */
export const useMsalWrapper = (): MsalWrapper => {
  const retrieveAccount = () => {
    const currentProfile = currentUserProfile.value;

    let account: AccountInfo | null = null;
    if (currentProfile?.accountId) {
      account = msalInstance.getAccountByHomeId(currentProfile.accountId);
    }
    // Fallback to active account if not found by homeId
    if (!account) {
      account = msalInstance.getActiveAccount();
    }

    return account;
  };

  const msalLogIn = async () => {
    try {
      const result = await msalInstance.loginPopup();
      let { account } = result;
      if (!account) {
        account = retrieveAccount();
      }
      return account;
    } catch (error: unknown) {
      const msalError = error as MsalError;
      const errorCode = msalError?.errorCode ?? msalError?.code;
      const errorMessage =
        msalError?.errorMessage ?? msalError?.message ?? 'Login failed';
      throw new LoginInfoError(errorMessage, errorCode, error);
    }
  };

  const msalLogOut = async () => {
    try {
      const { postLogoutRedirectUri } = msalConfig.auth;
      const account = retrieveAccount() ?? undefined;

      await msalInstance.logoutPopup({
        account,
        postLogoutRedirectUri,
        mainWindowRedirectUri: postLogoutRedirectUri,
      });
      console.info('Logout successful');
      return true;
    } catch (error) {
      console.error('Logout failed:', error);
      return false;
    }
  };

  const requiresInteraction = (errorCode?: string): boolean => {
    const interactionErrors = [
      'consent_required',
      'interaction_required',
      'login_required',
      'bad_token',
      'invalid_grant',
    ];
    return interactionErrors.includes(errorCode ?? '');
  };

  const handleTokenPopup = async (scopes: string[]) => {
    try {
      return await msalInstance.acquireTokenPopup({ scopes });
    } catch (popupError: unknown) {
      const msalError = popupError as MsalError;
      const errorCode = msalError?.errorCode ?? msalError?.code;
      const errorMessage =
        msalError?.errorMessage ??
        msalError?.message ??
        'Token acquisition failed';
      throw new LoginInfoError(errorMessage, errorCode, popupError);
    }
  };

  const acquireToken = async (scopes: string[]) => {
    let silentResult: AuthenticationResult | null = null;

    try {
      const account = retrieveAccount() ?? undefined;
      silentResult = await msalInstance.acquireTokenSilent({ scopes, account });
    } catch (error: unknown) {
      // InteractionRequiredAuthError covers all cases where MSAL requires user interaction,
      // including expired SPA refresh tokens (AADSTS700084 / bad_token)
      if (error instanceof InteractionRequiredAuthError) {
        return handleTokenPopup(scopes);
      }

      const errorCode = (error as { errorCode?: string })?.errorCode;

      if (requiresInteraction(errorCode)) {
        return handleTokenPopup(scopes);
      }

      const msalError = error as MsalError;
      const errorMessage =
        msalError?.errorMessage ??
        msalError?.message ??
        'Silent token retrieval failed';
      throw new LoginInfoError(errorMessage, errorCode, error);
    }

    // MSAL can swallow the 400/bad_token error internally and resolve with null
    // instead of throwing (e.g. expired SPA refresh token, AADSTS700084).
    // Fall back to the interactive popup in that case.
    if (!silentResult) {
      return handleTokenPopup(scopes);
    }

    return silentResult;
  };

  const getAccessTokenByScopes = async (scopes: string[]) => {
    const cacheKey = scopes.join(',');

    const cachedRequest = tokenRequestCache.get(cacheKey);
    if (cachedRequest) {
      return cachedRequest;
    }

    const requestPromise = acquireToken(scopes).finally(() => {
      tokenRequestCache.delete(cacheKey);
    });

    tokenRequestCache.set(cacheKey, requestPromise);
    return requestPromise;
  };

  return {
    msalLogOut,
    msalLogIn,
    getAccessTokenByScopes,
  };
};
